package dev.danko.roblox_craft_ai.uikit.components

import dev.danko.roblox_craft_ai.uikit.i18n.strings
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import dev.danko.roblox_craft_ai.uikit.theme.toAppPalette
import dev.danko.roblox_craft_ai.uikit.util.PageScrollLock
import kotlinx.browser.window
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.w3c.dom.events.Event

val ModalBackdropFadeInKeyframes = Keyframes {
    from {
        Modifier.opacity(0)
    }
    to {
        Modifier.opacity(1)
    }
}

val ModalSheetEnterKeyframes = Keyframes {
    from {
        Modifier
            .opacity(0)
            .scale(0.95)
    }
    to {
        Modifier
            .opacity(1)
            .scale(1.0)
    }
}

@Composable
fun ModalDialog(
    visible: Boolean,
    onCloseRequest: () -> Unit,
    title: String? = null,
    dismissOnBackdropClick: Boolean = true,
    dismissOnEsc: Boolean = true,
    footer: @Composable () -> Unit = {},
    modifier: Modifier = Modifier,
    /** Widest the sheet gets (it is 92 % of the screen on phones). */
    maxWidthRem: Double = 32.0,
    content: @Composable () -> Unit
) {
    if (!visible) return

    DisposableEffect(visible) {
        PageScrollLock.acquire()
        onDispose { PageScrollLock.release() }
    }

    DisposableEffect(visible, dismissOnEsc) {
        val listener: (Event) -> Unit = { event ->
            val keyboardEvent = event.asDynamic()
            if (dismissOnEsc && keyboardEvent.key == "Escape") {
                onCloseRequest()
            }
        }
        window.addEventListener("keydown", listener)
        onDispose {
            window.removeEventListener("keydown", listener)
        }
    }

    val palette = com.varabyte.kobweb.silk.theme.colors.ColorMode.current.toAppPalette()

    val backdropModifier = Modifier
        .position(Position.Fixed)
        .top(0.px)
        .left(0.px)
        .width(100.percent)
        .height(100.vh)
        .zIndex(9998)
        .backgroundColor(Color.rgba(0, 0, 0, 0.6f))
        .display(DisplayStyle.Flex)
        .justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center)
        .alignItems(org.jetbrains.compose.web.css.AlignItems.Center)
        .padding(1.cssRem)
        .animation(
            ModalBackdropFadeInKeyframes.toAnimation(
                duration = 200.ms
            )
        )
        .onClick {
            if (dismissOnBackdropClick) {
                onCloseRequest()
            }
        }

    val sheetModifier = Modifier
        .zIndex(9999)
        .width(92.percent)
        .maxWidth(maxWidthRem.cssRem)
        .maxHeight(90.vh)
        .display(DisplayStyle.Flex)
        .flexDirection(FlexDirection.Column)
        .backgroundColor(palette.surface)
        .border(1.px, LineStyle.Solid, palette.border)
        .borderRadius(1.cssRem)
        .boxShadow(0.px, 8.px, 32.px, 0.px, Color.rgba(0, 0, 0, 0.6f))
        .animation(
            ModalSheetEnterKeyframes.toAnimation(
                duration = 200.ms
            )
        )
        .onClick { event ->
            event.stopPropagation()
        }
        .then(modifier)

    Box(backdropModifier) {
        Box(sheetModifier) {
            if (title != null) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(topBottom = 1.cssRem, leftRight = 1.5.cssRem)
                        .borderBottom(1.px, LineStyle.Solid, palette.border),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    SpanText(
                        title,
                        Modifier
                            .fontSize(1.cssRem)
                            .fontWeight(FontWeight.SemiBold)
                            .color(palette.textPrimary)
                    )
                    AppButton(
                        text = "",
                        iconLeft = "✕",
                        variant = AppButtonVariant.OUTLINE,
                        size = AppButtonSize.SM,
                        onClick = onCloseRequest
                    )
                }
            }

            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(1.5.cssRem)
                    .overflow(Overflow.Auto)
            ) {
                content()
            }

            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(topBottom = 1.cssRem, leftRight = 1.5.cssRem)
                    .borderTop(1.px, LineStyle.Solid, palette.border)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth().gap(0.5.cssRem),
                    horizontalArrangement = Arrangement.End,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    footer()
                }
            }
        }
    }
}

@Composable
fun ModalDialogConfirmActions(
    confirmText: String = strings().common.confirm,
    cancelText: String = strings().common.cancel,
    onConfirm: () -> Unit,
    onCancel: () -> Unit,
    confirmVariant: AppButtonVariant = AppButtonVariant.PRIMARY,
    cancelVariant: AppButtonVariant = AppButtonVariant.OUTLINE,
    confirmLoading: Boolean = false,
    confirmEnabled: Boolean = true
): @Composable () -> Unit = {
    Row(
        modifier = Modifier.fillMaxWidth().gap(0.5.cssRem),
        horizontalArrangement = Arrangement.End,
        verticalAlignment = Alignment.CenterVertically
    ) {
        AppButton(
            text = cancelText,
            onClick = onCancel,
            variant = cancelVariant,
            size = AppButtonSize.MD
        )
        AppButton(
            text = confirmText,
            onClick = onConfirm,
            variant = confirmVariant,
            size = AppButtonSize.MD,
            loading = confirmLoading,
            enabled = confirmEnabled
        )
    }
}
